/**
 * Where a chat session began, for the conversation timeline.
 *
 * A session is one visit to the shop. The AGENT's history is cut at its start,
 * so two messages that look adjacent in this timeline can be separated by
 * everything the assistant knows. Without a marker the advisor reads a reply
 * that answers as though it had never met the customer and has no way to tell
 * why — the support ticket writes itself ("el bot se olvidó del cliente").
 */

export type SessionBoundary = {
  id: string;
  /** EXCLUSIVE: the visit holds every message with a higher `seq`. */
  fromSeq: number;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
};

export type SessionDivider = {
  label: string;
  /** Why the PREVIOUS visit ended, in the advisor's words. Null when unknown. */
  hint: string | null;
};

const END_REASON_HINT: Record<string, string> = {
  order_delivered: "la anterior se cerró al entregarse el pedido",
  idle: "la anterior se cerró por falta de actividad",
  max_age: "la anterior se cerró por su duración",
  merged: "la anterior se cerró al unir este prospecto con otro",
};

/**
 * Maps the message that OPENS each visit to its divider.
 *
 * Two rules, both about not lying to the reader:
 *
 *  - Pass the list you actually RENDER, like `groupByDay`. A divider computed
 *    over the unfiltered messages can land on a message the channel filter is
 *    hiding, and then it marks the wrong bubble.
 *  - The visit that opens the timeline gets NO divider. It would sit above the
 *    first message announcing a boundary that is really just the beginning of
 *    the thread, which is the one place a divider says nothing.
 */
export function sessionDividersByMessageId(
  messages: ReadonlyArray<{ id: string; seq: number }>,
  sessions: ReadonlyArray<SessionBoundary>,
): Map<string, SessionDivider> {
  const out = new Map<string, SessionDivider>();
  if (messages.length === 0 || sessions.length === 0) return out;

  // The endpoint serves them newest-first; the timeline reads oldest-first.
  const ordered = [...sessions].sort((a, b) => a.fromSeq - b.fromSeq);
  const firstRenderedId = messages[0]?.id;

  ordered.forEach((session, index) => {
    const opener = messages.find((m) => m.seq > session.fromSeq);
    if (!opener || opener.id === firstRenderedId) return;

    const previous = ordered[index - 1];
    out.set(opener.id, {
      label: "Nueva sesión",
      hint: previous?.endReason ? (END_REASON_HINT[previous.endReason] ?? null) : null,
    });
  });

  return out;
}
